import { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

const AIRBNB_MARCHMONT_URL =
  'https://www.airbnb.co.uk/s/Marchmont--Edinburgh/homes?refinement_paths%5B%5D=%2Fhomes&acp_id=df81e16d-2b3b-4b5f-9488-4ee184994c81&date_picker_type=calendar&search_type=user_map_move&flexible_trip_lengths%5B%5D=one_week&monthly_start_date=2026-05-01&monthly_length=3&monthly_end_date=2026-08-01&price_filter_input_type=2&channel=EXPLORE&zoom_level=14.667365322272348&price_filter_num_nights=2&place_id=ChIJcf84MHbHh0gRXCf4fB4nigw&query=Marchmont%2C%20Edinburgh&search_mode=regular_search&ne_lat=55.94982712434995&ne_lng=-3.183670933641565&sw_lat=55.92939615941262&sw_lng=-3.2088012854793533&zoom=14.667365322272348&search_by_map=true&flexible_trip_dates%5B%5D=december&source=structured_search_input_header&checkin=2026-12-30&checkout=2027-01-01';

const HOTEL_LINKS = {
  bruntsfield: 'https://www.thebruntsfield.co.uk/',
  aoEdinburgh: 'https://www.aohostels.com/en/edinburgh/edinburgh-city/',
  moxy: 'https://www.booking.com/hotel/gb/moxy-edinburgh-fountainbridge.en-gb.html',
  premierSuitesFountain:
    'https://www.premiersuiteseurope.com/en/locations/united-kingdom/edinburgh/serviced-apartments-fountain-court',
  hubHaymarket: 'https://www.premierinn.com/gb/en/hotels/scotland/lothian/edinburgh/hub-edinburgh-haymarket.html',
  hubRoyalMile: 'https://www.premierinn.com/gb/en/hotels/scotland/lothian/edinburgh/hub-edinburgh-royal-mile.html',
  travelodgeWaterloo: 'https://www.travelodge.co.uk/hotels/428/Edinburgh-Central-Waterloo-Place-hotel',
  craigie: 'https://thecraigie.co.uk/',
  premierDalkeith: 'https://www.premierinn.com/gb/en/hotels/scotland/lothian/edinburgh/edinburgh-a7-dalkeith.html',
  blackIvy: 'https://weareblackivy.com/',
  novotelCentre: 'https://all.accor.com/hotel/3271/index.en.shtml',
  residenceInn: 'https://www.marriott.com/en-us/hotels/ediri-residence-inn-edinburgh/overview/',
  sheraton: 'https://www.marriott.com/en-us/hotels/edigs-sheraton-grand-hotel-and-spa-edinburgh/overview/',
  premierLauriston:
    'https://www.premierinn.com/gb/en/hotels/scotland/lothian/edinburgh/edinburgh-city-centre-lauriston-place.html',
  motelOneRoyal: 'https://www.motel-one.com/en/hotels/edinburgh/hotel-edinburgh-royal/',
  hoxton: 'https://thehoxton.com/edinburgh/',
  marketStreet: 'https://marketstreethotel.com/',
};

function HotelLink({ href, children, className }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className ?? 'hotels-modal__hotel-link'}
    >
      {children}
    </a>
  );
}

function HotelPickRow({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="hotels-modal__pick-link" role="listitem">
      <span className="hotels-modal__pick-link-text">{children}</span>
    </a>
  );
}

/** Centred price-band title with Stay-accent marker SVGs. */
function HotelsTierTitle({ tierMarkerSrc, label, id }) {
  return (
    <h4 id={id} className="hotels-modal__tier-heading">
      <span className="hotels-modal__subhead-row hotels-modal__subhead-row--tier">
        <img
          src={tierMarkerSrc}
          alt=""
          className="hotels-modal__faq-marker"
          width="16"
          height="5"
          decoding="async"
          aria-hidden="true"
        />
        <span className="hotels-modal__tier-title-label">{label}</span>
        <img
          src={tierMarkerSrc}
          alt=""
          className="hotels-modal__faq-marker hotels-modal__faq-marker--flip"
          width="16"
          height="5"
          decoding="async"
          aria-hidden="true"
        />
      </span>
    </h4>
  );
}

/**
 * Full-screen overlay + scrollable panel for Stay section hotel guidance.
 * @param {{ open: boolean; onClose: () => void; dialogId: string; faqMarkerSrc: string; faqDividerSrc: string; tierMarkerSrc: string; hotelsTitleArtSrc: string; tapeArtSrc: string; paperTextureSrc: string }} props
 */
export function HotelsModal({
  open,
  onClose,
  dialogId,
  faqMarkerSrc,
  faqDividerSrc,
  tierMarkerSrc,
  hotelsTitleArtSrc,
  tapeArtSrc,
  paperTextureSrc,
}) {
  const titleId = useId();
  const closeBtnRef = useRef(null);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => closeBtnRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(t);
    };
  }, [open, onKeyDown]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="hotels-modal" role="presentation">
      <button
        type="button"
        className="hotels-modal__backdrop"
        aria-label="Close hotels guide"
        onClick={onClose}
      />
      <div className="hotels-modal__panel-shell">
        <div className="hotels-modal__tape" aria-hidden="true">
          <img src={tapeArtSrc} alt="" className="hotels-modal__tape-img" decoding="async" />
        </div>
        <div
          id={dialogId}
          className="hotels-modal__panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <button
            ref={closeBtnRef}
            type="button"
            className="hotels-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true">×</span>
          </button>
          <div
            className="hotels-modal__body"
            style={{
              '--hotels-modal-texture': `url("${new URL(paperTextureSrc, window.location.href).href}")`,
            }}
          >
          <div className="hotels-modal__intro-shell">
            <div className="hotels-modal__intro">
              <h2 id={titleId} className="hotels-modal__title">
                <img
                  src={hotelsTitleArtSrc}
                  alt="Hotels"
                  className="hotels-modal__title-art hotels-modal__title-art--hotels"
                  width="83"
                  height="66"
                  decoding="async"
                />
              </h2>
              <p className="hotels-modal__lead">
                We recommend booking accommodation as early as possible. Hogmanay is the busiest night of the year in
                Edinburgh.
              </p>
            </div>
          </div>

          <h3 className="hotels-modal__subhead hotels-modal__subhead--decorated">
            <span className="hotels-modal__subhead-row">
              <img
                src={faqMarkerSrc}
                alt=""
                className="hotels-modal__faq-marker"
                width="16"
                height="5"
                decoding="async"
                aria-hidden="true"
              />
              <span className="hotels-modal__subhead-text">Our recommendations</span>
              <img
                src={faqMarkerSrc}
                alt=""
                className="hotels-modal__faq-marker hotels-modal__faq-marker--flip"
                width="16"
                height="5"
                decoding="async"
                aria-hidden="true"
              />
            </span>
          </h3>

          <div className="hotels-modal__spotlights">
            <article className="hotels-modal__spotlight">
              <header className="hotels-modal__spotlight-head">
                <HotelLink href={HOTEL_LINKS.bruntsfield} className="hotels-modal__spotlight-title">
                  The JA Bruntsfield Hotel
                </HotelLink>
                <span className="hotels-modal__tier-pill" aria-hidden="true">
                  ££
                </span>
              </header>
              <p className="hotels-modal__spotlight-detail">
                Use discount code <strong>GA000718</strong> for 15% off. You’ll need to call or{' '}
                <a
                  href="mailto:reservations@thebruntsfield.co.uk"
                  className="hotels-modal__inline-link"
                  aria-label="Email reservations@thebruntsfield.co.uk"
                >
                  email
                </a>{' '}
                to book with the discount.
              </p>
            </article>
            <article className="hotels-modal__spotlight hotels-modal__spotlight--compact">
              <HotelLink href={AIRBNB_MARCHMONT_URL} className="hotels-modal__spotlight-title">
                Airbnbs in Marchmont / Meadows area
              </HotelLink>
            </article>
          </div>

          <p className="hotels-modal__note">
            We know that accommodation over New Year can be at a premium, so we’ve done our best to share options in
            all ranges for those attending and travelling from further afield.
          </p>

          <section className="hotels-modal__tier-block" aria-labelledby="hotels-tier-pound">
            <HotelsTierTitle id="hotels-tier-pound" tierMarkerSrc={tierMarkerSrc} label="£" />
            <div className="hotels-modal__picks" role="list">
              <HotelPickRow href={HOTEL_LINKS.aoEdinburgh}>a&amp;o Edinburgh City</HotelPickRow>
              <HotelPickRow href={HOTEL_LINKS.moxy}>Moxy Edinburgh</HotelPickRow>
              <HotelPickRow href={HOTEL_LINKS.premierSuitesFountain}>
                PREMIER SUITES Edinburgh Fountain Court
              </HotelPickRow>
              <div className="hotels-modal__pick hotels-modal__pick--hub" role="listitem">
                <span className="hotels-modal__hub-line">hub by Premier Inn — </span>
                <HotelLink href={HOTEL_LINKS.hubHaymarket} className="hotels-modal__hub-link">
                  Haymarket
                </HotelLink>
                <span className="hotels-modal__hub-sep"> or </span>
                <HotelLink href={HOTEL_LINKS.hubRoyalMile} className="hotels-modal__hub-link">
                  Royal Mile
                </HotelLink>
              </div>
              <HotelPickRow href={HOTEL_LINKS.travelodgeWaterloo}>
                Travelodge Edinburgh Central Waterloo Place
              </HotelPickRow>
              <HotelPickRow href={HOTEL_LINKS.craigie}>The Craigie Hotel, Penicuik</HotelPickRow>
              <HotelPickRow href={HOTEL_LINKS.premierDalkeith}>Premier Inn, Dalkeith</HotelPickRow>
            </div>
          </section>

          <section className="hotels-modal__tier-block" aria-labelledby="hotels-tier-pound-pound">
            <HotelsTierTitle id="hotels-tier-pound-pound" tierMarkerSrc={tierMarkerSrc} label="££" />
            <div className="hotels-modal__picks" role="list">
              <HotelPickRow href={HOTEL_LINKS.blackIvy}>Black Ivy, Edinburgh</HotelPickRow>
              <HotelPickRow href={HOTEL_LINKS.novotelCentre}>Novotel Edinburgh Centre</HotelPickRow>
              <HotelPickRow href={HOTEL_LINKS.residenceInn}>Residence Inn by Marriott Edinburgh</HotelPickRow>
              <HotelPickRow href={HOTEL_LINKS.sheraton}>Sheraton Grand Hotel &amp; Spa Edinburgh</HotelPickRow>
              <HotelPickRow href={HOTEL_LINKS.premierLauriston}>
                Premier Inn Edinburgh Central (Lauriston Place)
              </HotelPickRow>
              <HotelPickRow href={HOTEL_LINKS.motelOneRoyal}>Motel One Edinburgh-Royal</HotelPickRow>
            </div>
          </section>

          <section className="hotels-modal__tier-block" aria-labelledby="hotels-tier-pound-triple">
            <HotelsTierTitle id="hotels-tier-pound-triple" tierMarkerSrc={tierMarkerSrc} label="£££" />
            <div className="hotels-modal__picks" role="list">
              <HotelPickRow href={HOTEL_LINKS.hoxton}>The Hoxton Edinburgh</HotelPickRow>
              <HotelPickRow href={HOTEL_LINKS.marketStreet}>Market Street Hotel, Edinburgh</HotelPickRow>
            </div>
          </section>

          <div className="hotels-modal__notes-intro">
            <img
              src={faqDividerSrc}
              alt=""
              className="hotels-modal__notes-divider"
              width="18"
              height="15"
              decoding="async"
              aria-hidden="true"
            />
            <h3 className="hotels-modal__subhead hotels-modal__subhead--decorated hotels-modal__subhead--notes">
              <span className="hotels-modal__subhead-row">
                <img
                  src={faqMarkerSrc}
                  alt=""
                  className="hotels-modal__faq-marker"
                  width="16"
                  height="5"
                  decoding="async"
                  aria-hidden="true"
                />
                <span className="hotels-modal__subhead-text">Helpful notes</span>
                <img
                  src={faqMarkerSrc}
                  alt=""
                  className="hotels-modal__faq-marker hotels-modal__faq-marker--flip"
                  width="16"
                  height="5"
                  decoding="async"
                  aria-hidden="true"
                />
              </span>
            </h3>
          </div>
          <div className="hotels-modal__tips">
            <p className="hotels-modal__tip">
              Edinburgh is very walkable, but there are a few hills and cobbled streets, so something to bear in mind
              when planning your route.
            </p>
            <p className="hotels-modal__tip">
              We suggest booking a flexible rate where possible. Although this might be slightly more initially, it gives
              you the option to change your booking if prices drop closer to the date.
            </p>
          </div>
        </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
